import { Link } from 'react-router-dom';
import { CalendarEvent } from '../../../mocks/calendarEvents';
import RsvpButton from '../../../components/feature/RsvpButton';
import FavoriteButton from '../../../components/FavoriteButton';

interface EventListProps {
  events: CalendarEvent[];
}

const categoryBadgeColors: Record<string, string> = {
  Concert: 'bg-amber-100 text-amber-700',
  Festival: 'bg-rose-100 text-rose-700',
  Market: 'bg-emerald-100 text-emerald-700',
  Family: 'bg-sky-100 text-sky-700',
  Sports: 'bg-orange-100 text-orange-700',
  'Arts & Culture': 'bg-violet-100 text-violet-700',
  Community: 'bg-cyan-100 text-cyan-700',
  Holiday: 'bg-red-100 text-red-700',
};

function formatDate(dateStr: string): { day: string; month: string; weekday: string } {
  const d = new Date(dateStr + 'T00:00:00');
  return {
    day: String(d.getDate()),
    month: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
    weekday: d.toLocaleString('en-US', { weekday: 'short' }),
  };
}

export default function EventList({ events, selectedCategory, selectedDate }: EventListProps) {
  if (events.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ri-calendar-close-line text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Events Found</h3>
        <p className="text-sm text-gray-500">Try adjusting your filters or selecting a different date range.</p>
      </div>
    );
  }

  // Group events by date
  const grouped: Record<string, CalendarEvent[]> = {};
  events.forEach((ev) => {
    if (!grouped[ev.date]) grouped[ev.date] = [];
    grouped[ev.date].push(ev);
  });

  const sortedDates = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      {/* Promote Event CTA */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-megaphone-line text-2xl"></i>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-1">Promote Your Event</h3>
              <p className="text-sm text-white/90">Reach thousands of local residents. Plans start at just $25.</p>
            </div>
          </div>
          <a
            href="/promote-event"
            className="bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap inline-flex items-center gap-2"
          >
            <span>Learn More</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>

      {sortedDates.map((date) => {
        const { day, month, weekday } = formatDate(date);
        const dayEvents = grouped[date];

        return (
          <div key={date}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-calendar-line text-teal-600"></i>
              {date}
            </h3>
            <div className="space-y-4">
              {dayEvents.map((event) => {
                // Determine if event is promoted (for demo, mark some events as promoted)
                const isPromoted = event.id % 3 === 0;
                const isFeatured = event.id % 5 === 0;

                return (
                  <div
                    key={event.id}
                    className={`bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all border-2 ${
                      isFeatured ? 'border-amber-300' : isPromoted ? 'border-emerald-300' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover object-top"
                        />
                        {isFeatured && (
                          <div className="absolute top-3 left-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <i className="ri-vip-crown-fill"></i>
                            <span>FEATURED</span>
                          </div>
                        )}
                        {isPromoted && !isFeatured && (
                          <div className="absolute top-3 left-3 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <i className="ri-star-fill"></i>
                            <span>PROMOTED</span>
                          </div>
                        )}
                        <FavoriteButton
                          itemId={event.id}
                          itemType="event"
                          className="absolute top-3 right-3"
                        />
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium whitespace-nowrap">
                                {event.category}
                              </span>
                            </div>
                            <Link to={`/events/${event.id}`}>
                              <h4 className="text-xl font-bold text-gray-900 mb-2 hover:text-teal-600 transition-colors">
                                {event.title}
                              </h4>
                            </Link>
                            <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                <span>{event.time}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-map-pin-line"></i>
                                <span>{event.location}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-4">
                          <RsvpButton eventId={event.id} variant="secondary" />
                          <Link
                            to={`/events/${event.id}`}
                            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:border-teal-600 hover:text-teal-600 transition-colors whitespace-nowrap"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
